/**
 * Classify vault files for v2 per-file jobs (first pass).
 * Photos / videos: never sent to AI. Duplicates (same name + type + size): ignored.
 */

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i;
const VIDEO_EXT = /\.(mp4|m4v|mov|avi|wmv|flv|webm|mkv|mpeg|mpg|3gp|ogv)$/i;

export function isLikelyPhotoFilename(name) {
  return IMAGE_EXT.test(String(name || ''));
}

export function isLikelyVideoFilename(name) {
  return VIDEO_EXT.test(String(name || ''));
}

export function isLikelyPhotoFile(file = {}) {
  const type = String(file.contentType || file.type || file.mimeType || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  return isLikelyPhotoFilename(file.name);
}

export function isLikelyVideoFile(file = {}) {
  const type = String(file.contentType || file.type || file.mimeType || '').toLowerCase();
  if (type.startsWith('video/')) return true;
  return isLikelyVideoFilename(file.name);
}

/**
 * Fingerprint for duplicate detection.
 * Requires name + size. Type is included when present so same name/size
 * with different MIME types are treated as different files.
 * Returns null when size is unknown — do not treat as duplicate.
 */
export function evidenceFingerprint(file = {}) {
  const name = String(file.name || '').trim().toLowerCase();
  const sizeRaw = file.size ?? file.byteSize ?? file.bytes;
  const size = sizeRaw === undefined || sizeRaw === null || sizeRaw === '' ? null : Number(sizeRaw);
  if (!name || size == null || Number.isNaN(size)) return null;
  const type = String(file.contentType || file.type || file.mimeType || '')
    .trim()
    .toLowerCase();
  return `${name}|${type}|${size}`;
}

function inferKind(name, contentType) {
  if (isLikelyPhotoFile({ name, contentType })) return 'photo';
  if (isLikelyVideoFile({ name, contentType })) return 'video';
  const lower = String(name || '').toLowerCase();
  const type = String(contentType || '').toLowerCase();
  if (type.includes('pdf') || lower.endsWith('.pdf')) return 'pdf';
  if (type.includes('sheet') || type.includes('excel') || /\.(xlsx?|csv|ods)$/i.test(lower)) {
    return 'spreadsheet';
  }
  if (type.includes('presentation') || type.includes('powerpoint') || /\.(pptx?|odp)$/i.test(lower)) {
    return 'presentation';
  }
  if (/\.(docx?|rtf|txt|odt)$/i.test(lower) || type.includes('word') || type.startsWith('text/')) {
    return 'document';
  }
  return 'document';
}

/**
 * Classify a single vault row (no duplicate logic).
 */
export function classifyEvidenceFile(file = {}) {
  const name = String(file.name || 'unnamed');
  const contentType = file.contentType || file.type || file.mimeType || null;
  const sizeRaw = file.size ?? file.byteSize ?? file.bytes;
  const size =
    sizeRaw === undefined || sizeRaw === null || sizeRaw === '' ? null : Number(sizeRaw);

  const base = {
    name,
    url: file.url || null,
    tags: Array.isArray(file.tags) ? file.tags : [],
    contentType: contentType || null,
    size: size != null && !Number.isNaN(size) ? size : null,
    fingerprint: null,
  };
  base.fingerprint = evidenceFingerprint(base);
  base.kind = inferKind(name, contentType);

  if (isLikelyPhotoFile(base)) {
    return {
      ...base,
      kind: 'photo',
      status: 'skipped_photo_policy',
      detail: 'Photo — skipped for safeguarding (human review only; never sent to AI).',
    };
  }

  if (isLikelyVideoFile(base)) {
    return {
      ...base,
      kind: 'video',
      status: 'skipped_video',
      detail: 'Video — skipped (not sent to AI; human review only).',
    };
  }

  return {
    ...base,
    status: 'queued',
    detail: 'Queued for per-file content analysis.',
  };
}

/**
 * First pass over the vault: photos/videos skipped, then true duplicates ignored.
 * Duplicate = same name + type + size as an earlier file (fingerprint).
 * Same name alone is not enough (different size/type stay queued).
 */
export function buildClassifiedEvidenceFiles(vaultFiles = []) {
  const seen = new Map(); // fingerprint -> 1-based display index of first
  return (Array.isArray(vaultFiles) ? vaultFiles : []).map((raw, index) => {
    const classified = classifyEvidenceFile(raw);
    const fp = classified.fingerprint;
    if (fp && seen.has(fp)) {
      const firstN = seen.get(fp);
      return {
        ...classified,
        status: 'skipped_duplicate',
        detail: `Duplicate of file ${firstN} (same name, type, and size) — ignored.`,
        duplicateOf: firstN,
      };
    }
    if (fp) seen.set(fp, index + 1);
    return classified;
  });
}

export function countEvidenceJobStatuses(files = []) {
  const list = Array.isArray(files) ? files : [];
  const skippedPhoto = list.filter((f) => f.status === 'skipped_photo_policy').length;
  const skippedVideo = list.filter((f) => f.status === 'skipped_video').length;
  const skippedDuplicate = list.filter((f) => f.status === 'skipped_duplicate').length;
  const failed = list.filter((f) => f.status === 'failed').length;
  const queued = list.filter((f) => f.status === 'queued').length;
  const analysing = list.filter(
    (f) => f.status === 'analysing' || f.status === 'analyzing'
  ).length;
  const extracted = list.filter((f) => f.status === 'extracted' || f.status === 'done').length;
  const skipped = skippedPhoto + skippedVideo + skippedDuplicate;
  const done = skipped + failed + extracted;
  return {
    skippedPhoto,
    skippedVideo,
    skippedDuplicate,
    skipped,
    failed,
    queued,
    analysing,
    extracted,
    done,
    total: list.length,
  };
}
